import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@kognitos/lattice';

import { ChatActivity } from '@/shared/components/ChatActivity';

import type { TRainToolCallStatus } from '../../components/RainToolCallCard';
import type {
  IAutomationRunOutput,
  TAutomationRunState,
} from '../automation-tools';

export interface IRainAutomationRunCardProps {
  /** The automation's user-facing name, derived from its tool name. */
  automationName: string;
  /** Whether the TOOL CALL resolved — not how the run itself went. */
  status: TRainToolCallStatus;
  /**
   * How the RUN settled, when the result said. Absent on an unfamiliar result
   * envelope, in which case the card reports the tool call's own outcome.
   */
  runState?: TAutomationRunState;
  /**
   * What the run produced, one entry per output key. These are the point of
   * having run it, so they render as a labelled list rather than as JSON.
   */
  outputs?: readonly IAutomationRunOutput[];
  /** The failure text, when the run or the call failed. */
  errorMessage?: string;
}

/**
 * One automation run, as it happens, in the agent's own chat.
 *
 * This is the card an END USER of a built agent sees when their agent runs one
 * of the workspace's automations — the surface Quill already has for its test
 * runs (`RainTestRunCard`) and which a target agent had nothing equivalent for:
 * an automation run used to render as the GENERIC tool card, i.e. its raw
 * arguments dumped into the transcript with no state, no outcome and no outputs.
 *
 * A run takes real time and can change real records, so the three things it
 * reports are deliberately the three a waiting user asks for: WHICH automation,
 * WHERE IT GOT TO, and WHAT IT PRODUCED.
 *
 * Unlike Quill's card this one does NOT link out to the run view. It could not:
 * the deep link needs the automation's id (`AUTOMATION_RUN` takes
 * `automationId` + `runId`) and an automation tool result is not guaranteed to
 * carry one. A link that resolves for some automations and 404s for others is
 * worse than none, so the outputs are surfaced inline instead.
 */
export function RainAutomationRunCard({
  automationName,
  status,
  runState,
  outputs,
  errorMessage,
}: IRainAutomationRunCardProps) {
  const { t } = useTranslation('agent-builder');

  // The run's own verdict outranks the call's: a tool call can resolve
  // perfectly well while reporting a run that failed.
  const isFailed = runState === 'FAILED' || status === 'error';
  const isStopped = runState === 'STOPPED';
  const isAwaitingGuidance = runState === 'AWAITING_GUIDANCE';
  // Still moving: either the call is in flight, or it resolved with a run that
  // has not settled yet (the agent polls for that).
  const isRunning =
    !isFailed &&
    !isStopped &&
    !isAwaitingGuidance &&
    (status === 'running' ||
      runState === 'PENDING' ||
      runState === 'EXECUTING');

  let title: string;
  if (isFailed) {
    title = t('agentBuilder.automationRun.failed', { name: automationName });
  } else if (isStopped) {
    title = t('agentBuilder.automationRun.stopped', { name: automationName });
  } else if (isAwaitingGuidance) {
    title = t('agentBuilder.automationRun.awaitingGuidance', {
      name: automationName,
    });
  } else if (isRunning) {
    title = t('agentBuilder.automationRun.running', { name: automationName });
  } else {
    title = t('agentBuilder.automationRun.completed', { name: automationName });
  }

  const icon = isRunning ? 'Loader2' : isFailed ? 'X' : 'Zap';

  // A run waiting on a person is neither done nor broken, and the error
  // treatment would read as broken — so it is called out with a badge instead.
  const titleSuffix = isAwaitingGuidance ? (
    <Badge variant="outline">
      {t('agentBuilder.automationRun.needsInputBadge')}
    </Badge>
  ) : undefined;

  // A failure explains itself; otherwise the outputs are the payload. Never
  // both: an error message alongside a partial output list reads as though the
  // run half-succeeded.
  const hasOutputs = !isFailed && outputs !== undefined && outputs.length > 0;
  const hasDetail = isFailed ? Boolean(errorMessage) : hasOutputs;

  return (
    <ChatActivity
      variant="tool-call"
      compact
      title={title}
      titleSuffix={titleSuffix}
      isActive={isRunning}
      isError={isFailed}
      icon={icon}
      collapsible={hasDetail}
      // Outputs open by default: they are what the user was waiting for, and a
      // completed run whose results are hidden behind a chevron is the problem
      // this card exists to fix. A failure stays collapsed — the message is
      // already in the title.
      defaultExpanded={hasOutputs}
      // Never close a pane the reader opened themselves.
      collapseOnComplete={false}
      data-testid="rain-automation-run-card"
      data-status={status}
      data-run-state={runState ?? ''}
      data-output-count={hasOutputs ? outputs.length : 0}
    >
      {isFailed
        ? errorMessage && (
            <pre className="whitespace-pre-wrap break-words text-xs text-destructive">
              {errorMessage}
            </pre>
          )
        : hasOutputs && (
            <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
              {outputs.map(output => (
                <Fragment key={output.name}>
                  <dt className="truncate font-medium text-muted-foreground">
                    {output.name}
                  </dt>
                  <dd className="break-words text-foreground">
                    {output.value}
                  </dd>
                </Fragment>
              ))}
            </dl>
          )}
    </ChatActivity>
  );
}
